import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { ProjectPriority } from '@/types/projects';

export interface Project {
  id: UUID;
  name: string;
  description?: string;
  start_date: string;
  end_date: string;
  priority: ProjectPriority;
  status: string;
  owner_id: string;
  owner?: {
    id: string;
    full_name: string;
    email: string;
  };
  template_id?: string;
  created_at: string;
  updated_at: string;
}

export function useProjects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const fetchProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, start_date, end_date, priority, status, owner_id, template_id, created_at, updated_at')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getProjectById = useCallback(async (id: UUID) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, description, start_date, end_date, priority, status, owner_id, template_id, created_at, updated_at')
        .eq('id', id)
        .single();

      if (error) throw error;
      setSelectedProject(data);
      return data;
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err as Error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return { 
    projects, 
    selectedProject,
    getProjectById,
    isLoading, 
    error 
  };
}
